import { useParams } from "react-router-dom";
import { FormItem } from "../../types/formType";
import { useEffect, useState } from "react";
import FormApis from "../../api/formApis";
import FormFieldRenderer from "../../components/FromFieldRenderer";
import StepsBar, { StepperActions } from "../../components/StepsBar";
import { toast } from "sonner";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useSelector } from "react-redux";
import { getSchoolBranchByCode } from "../../features/school-branch-controller/schoolBranchSlice";

const mapQuestionToField = (q: any) => ({
  id: q.questionId,
  fieldName: q.question,
  fieldAbbr: `q_${q.questionId}`,
  fieldType: q.questionType,
  fieldDescription: q.question,
  isFieldRequired: q.isMandatory,
  options: q.options || [],
});

export default function FormLink() {
  const { formSeqId } = useParams<{ formSeqId: string }>();
  const [form, setForm] = useState<FormItem | null>(null);
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<any>({});
  const { branchCode } = useParams();

  const dispatch = useDispatch<AppDispatch>();
  const { branchDetails } = useSelector(
    (state: RootState) => state.schoolBranch,
  );

  useEffect(() => {
    dispatch(getSchoolBranchByCode());
  }, [dispatch, branchCode]);

  useEffect(() => {
    if (!formSeqId) return;

    (async () => {
      const res = await FormApis.getFormBySeqId(formSeqId);

      if (res?.status === 1 && res.data) {
        setForm(res.data);
      }
    })();
  }, [formSeqId]);

  const sections = form?.formSections || [];
  const currentSection = sections[step];

  const isSectionValid = () => {
    if (!currentSection) return false;

    return currentSection.formQuestions.every((q: any) => {
      if (!q.isMandatory) return true;

      const key = `q_${q.questionId}`;
      const value = formData[key];

      if (value === undefined || value === null) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      if (Array.isArray(value) && value.length === 0) return false;

      return true;
    });
  };

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const totalSteps = sections.length;
  const isFirst = step === 0;
  const isLast = step === totalSteps - 1;

  const onNext = () => {
    if (!isLast) {
      setStep((prev) => prev + 1);
    }
  };

  const onBack = () => {
    if (!isFirst) {
      setStep((prev) => prev - 1);
    }
  };

  const buildAnswersPayload = () => {
    return Object.entries(formData).map(([key, value]) => ({
      questionId: Number(key.replace("q_", "")),
      answer: value,
    }));
  };

  const handleSubmit = async () => {
    if (!form || !formSeqId) return;

    try {
      const payload = {
        formEntryId: form.formEntryId ?? 0,
        formSeqId,
        answers: buildAnswersPayload(),
      };

      const res = await FormApis.submitFormResponse(payload);

      if (res?.status === 1) {
        toast.success("Form submitted successfully");
      } else {
        toast.error("Submission failed");
      }
    } catch {
      // error toast already handled
    }
  };

  return (
    <main className="min-h-screen bg-background-100 text-neutral-400 p-5">
      <section className="max-w-4xl mx-auto flex flex-col gap-5">
        <header className="flex flex-col items-center gap-2">
          <img
            src={branchDetails?.schoolBranchIcon}
            alt="logo"
            className="h-24"
          />
          <h3 className="text-2xl font-semibold">
            {branchDetails?.schoolBranchName}
          </h3>
          <p className="text-sm text-neutral-200">
            Kindly provide the information requested below.
          </p>
        </header>
        <section className="flex flex-col gap-2">
          <div className="bg-background-100 rounded-xl shadow-md p-6 flex flex-col gap-5">
            <h3 className="text-lg font-semibold text-neutral-500">
              {form?.formName}
            </h3>

            {/* Steps */}
            <div className="px-10 flex justify-center items-center">
              {sections.length > 0 && (
                <StepsBar
                  current={step + 1}
                  sections={sections.map((s: any) => ({
                    id: s.formSectionId,
                    name: s.sectionName,
                  }))}
                  onStepClick={(clickedStep) => setStep(clickedStep - 1)}
                />
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {sections.length === 0 && (
              <p className="text-center text-neutral-300">
                This form has no sections yet
              </p>
            )}
            <div className="grid gap-2">
              {currentSection?.formQuestions?.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-neutral-300 text-sm">
                    No questions available for this section right now.
                  </p>
                </div>
              ) : (
                currentSection?.formQuestions?.map((q: any) => (
                  <div
                    key={q.questionId}
                    className="bg-background-100 rounded-xl shadow-md  p-6"
                  >
                    <FormFieldRenderer
                      field={mapQuestionToField(q)}
                      formData={formData}
                      onChange={handleChange}
                      setFormData={setFormData}
                    />
                  </div>
                ))
              )}
            </div>

            <StepperActions
              isFirst={isFirst}
              isLast={isLast}
              isNextDisabled={!isSectionValid()}
              onBack={onBack}
              onNext={onNext}
              onSubmit={handleSubmit}
            />
          </div>
        </section>
      </section>
    </main>
  );
}
